import type { ENU } from '../contracts';

export interface KalmanOptions {
  /**
   * White-acceleration noise spectral density per axis (m^2/s^3). ~0.5 lets a
   * boat turn (v * omega ~ 1 m/s^2) without much lag while still averaging
   * GPS noise at 1 Hz.
   */
  accelPsd: number;
  /** Per-axis measurement sigma = accuracy * accuracyScale (accuracy = 68 % radius). */
  accuracyScale: number;
  minAccuracyM: number;
  /** Velocity sigma (m/s) on (re)initialisation. */
  initialVelSigma: number;
  /** Innovation gate (chi^2, 2 dof). Rejected fixes count towards maxRejects. */
  gateChi2: number;
  /** After this many consecutive rejected fixes the filter re-initialises on the new fix. */
  maxRejects: number;
  /** Re-initialise if two fixes are further apart than this (ms). */
  maxGapMs: number;
}

export const DEFAULT_KALMAN_OPTIONS: KalmanOptions = {
  accelPsd: 0.5,
  accuracyScale: 1,
  minAccuracyM: 1,
  initialVelSigma: 3,
  gateChi2: 30,
  maxRejects: 3,
  maxGapMs: 30000,
};

/**
 * 2D constant-velocity Kalman filter in a local ENU frame.
 *
 * State [e, n, ve, vn] at time `t` (ms). Measurement and process noise are
 * isotropic, so both axes share one 2x2 covariance [[p00, p01], [p01, p11]]
 * (exactly equivalent to the 4x4 filter with block-diagonal structure).
 */
export class KalmanCV2D {
  readonly opts: KalmanOptions;
  initialized = false;
  e = 0;
  n = 0;
  ve = 0;
  vn = 0;
  /** Time of the state (ms). */
  t = 0;
  p00 = 0;
  p01 = 0;
  p11 = 0;
  rejects = 0;

  constructor(opts: Partial<KalmanOptions> = {}) {
    this.opts = { ...DEFAULT_KALMAN_OPTIONS, ...opts };
  }

  reset(): void {
    this.initialized = false;
    this.rejects = 0;
  }

  /** Measurement variance per axis for a given accuracy radius. */
  measurementVar(accuracy: number): number {
    const s = Math.max(accuracy, this.opts.minAccuracyM) * this.opts.accuracyScale;
    return s * s;
  }

  init(e: number, n: number, accuracy: number, tMs: number): void {
    this.e = e;
    this.n = n;
    this.ve = 0;
    this.vn = 0;
    this.t = tMs;
    this.p00 = this.measurementVar(accuracy);
    this.p01 = 0;
    this.p11 = this.opts.initialVelSigma * this.opts.initialVelSigma;
    this.rejects = 0;
    this.initialized = true;
  }

  /** Advance the state to tMs (no-op for tMs <= t). */
  predict(tMs: number): void {
    const dt = (tMs - this.t) / 1000;
    if (dt <= 0) return;
    const q = this.opts.accelPsd;
    this.e += this.ve * dt;
    this.n += this.vn * dt;
    const dt2 = dt * dt;
    this.p00 += 2 * dt * this.p01 + dt2 * this.p11 + (q * dt2 * dt) / 3;
    this.p01 += dt * this.p11 + (q * dt2) / 2;
    this.p11 += q * dt;
    this.t = tMs;
  }

  /**
   * Position measurement. Returns false if the fix was rejected by the
   * innovation gate (the filter re-initialises after `maxRejects` in a row).
   */
  updatePosition(e: number, n: number, accuracy: number, tMs: number): boolean {
    if (!this.initialized || tMs - this.t > this.opts.maxGapMs) {
      this.init(e, n, accuracy, tMs);
      return true;
    }
    this.predict(tMs);
    const r = this.measurementVar(accuracy);
    const ye = e - this.e;
    const yn = n - this.n;
    const s = this.p00 + r;
    if ((ye * ye + yn * yn) / s > this.opts.gateChi2) {
      this.rejects++;
      if (this.rejects >= this.opts.maxRejects) {
        this.init(e, n, accuracy, tMs);
        return true;
      }
      return false;
    }
    this.rejects = 0;
    const k0 = this.p00 / s;
    const k1 = this.p01 / s;
    this.e += k0 * ye;
    this.n += k0 * yn;
    this.ve += k1 * ye;
    this.vn += k1 * yn;
    const p00 = this.p00, p01 = this.p01;
    this.p00 = (1 - k0) * p00;
    this.p01 = (1 - k0) * p01;
    this.p11 -= k1 * p01;
    return true;
  }

  /** Velocity measurement (e.g. GPS Doppler speed + course) at the current state time. */
  updateVelocity(ve: number, vn: number, sigma: number): void {
    if (!this.initialized) return;
    const s = this.p11 + sigma * sigma;
    const k0 = this.p01 / s;
    const k1 = this.p11 / s;
    const ye = ve - this.ve;
    const yn = vn - this.vn;
    this.e += k0 * ye;
    this.n += k0 * yn;
    this.ve += k1 * ye;
    this.vn += k1 * yn;
    const p01 = this.p01, p11 = this.p11;
    this.p00 -= k0 * p01;
    this.p01 -= k0 * p11;
    this.p11 = (1 - k1) * p11;
  }

  /**
   * Position predicted to tMs without changing the state. The horizon is
   * clamped to [-1 s, maxAheadMs] so a stalled GPS does not extrapolate away.
   */
  predictedPosition(tMs: number, out: ENU, maxAheadMs = 3000): ENU {
    let dt = tMs - this.t;
    if (dt > maxAheadMs) dt = maxAheadMs;
    else if (dt < -1000) dt = -1000;
    dt /= 1000;
    out.e = this.e + this.ve * dt;
    out.n = this.n + this.vn * dt;
    return out;
  }

  /** 1-sigma per-axis position uncertainty (m) at the state time. */
  positionSigma(): number {
    return Math.sqrt(this.p00);
  }

  speed(): number {
    return Math.sqrt(this.ve * this.ve + this.vn * this.vn);
  }
}
